import {DataGroup, DataItem, TimelineTimeAxisScaleType} from 'vis-timeline/esnext';
import {DataSet} from 'vis-data/esnext';
import {Mechanics} from '@/views/timeline/services/mechanics';
import {
    IndexedNestedTimelineDataStoreServiceType,
    ObserverType
} from '@/views/timeline/services/timeline-data-store-service.type';
import {MECHANICS_SCALE_SETTINGS} from '@/views/timeline/services/mechanics/staticdata/scales';
import {REGIONS} from '@/views/timeline/services/regions';
import {MECHANIC_BOOKING_TO_VIS_DATA_ITEM_TRANSFORMER_INSTANCE} from '@/views/timeline/services/mechanics/staticdata/mechanic-booking-to-vis-dataItem-transformer.service';
import {MechanicBookingType} from '@/views/timeline/services/booking.types';
import {STATIC_MECHANIC_BOOKINGS} from '@/views/timeline/services/mechanics/mechanics-booking-gen.service';
import {ScaleType} from '@/views/timeline/services/scale.type';
import {MECHANIC_BOOKING_TO_VIS_DATA_ITEM_SIMPLIFIED_TRANSFORMER_INSTANCE} from '@/views/timeline/services/mechanics/staticdatasimplified/mechanic-booking-to-vis-dataItem-simplified-transformer.service';
import {MECHANICS_SCALE_SETTINGS_SIMPLIFIED} from '@/views/timeline/services/mechanics/staticdatasimplified/scales';

class MechanicsTimelineDataStoreService
    implements IndexedNestedTimelineDataStoreServiceType<MechanicBookingType, DataGroup, DataGroup> {

    private regions: DataGroup[] = [];
    private mechanics: DataGroup[] = [];
    private items: DataSet<DataItem> | null = null;
    private itemsSimplified: DataSet<DataItem> | null = null;
    private readonly itemIndex: Map<number | string, MechanicBookingType> =
        new Map<number | string, MechanicBookingType>();
    private readonly groupIndex: Map<number | string, DataGroup> =
        new Map<number | string, DataGroup>();
    private readonly superGroupByGroupIndex: Map<number | string, DataGroup> =
        new Map<number | string, DataGroup>();
    private readonly superGroupIndex: Map<number | string, DataGroup> =
        new Map<number | string, DataGroup>();
    private dataObservers: ObserverType[] = [];

    getMechanics(): DataGroup[] {
        if (!this.mechanics || !this.mechanics.length) {
            this.mechanics = Mechanics;
        }
        return this.mechanics;
    }

    getRegions(): DataGroup[] {
        if (!this.regions || !this.regions.length) {
            this.regions = REGIONS;
        }
        return this.regions;
    }

    getGroups(): DataSet<DataGroup> {
        return new DataSet<DataGroup>(this.getMechanics().concat(this.getRegions()));
    }

    getItems(): DataSet<DataItem> {
        if (!this.items || !this.items.length) {
            const mechanicsBookingsDataSet = new DataSet<DataItem>();

            for (const mechanic of STATIC_MECHANIC_BOOKINGS) {
                mechanicsBookingsDataSet.add(
                    MECHANIC_BOOKING_TO_VIS_DATA_ITEM_TRANSFORMER_INSTANCE.transform(mechanic)
                );
            }

            this.items = mechanicsBookingsDataSet;
        }
        return this.items;
    }

    getItemsSimplified(): DataSet<DataItem> {
        if (!this.itemsSimplified || !this.itemsSimplified.length) {
            const mechanicsBookingsDataSet = new DataSet<DataItem>();

            for (const mechanic of STATIC_MECHANIC_BOOKINGS) {
                mechanicsBookingsDataSet.add(
                    MECHANIC_BOOKING_TO_VIS_DATA_ITEM_SIMPLIFIED_TRANSFORMER_INSTANCE.transform(mechanic)
                );
            }

            this.itemsSimplified = mechanicsBookingsDataSet;
        }
        return this.itemsSimplified;
    }

    getItemIndex(): Map<number | string, MechanicBookingType> {
        if (!this.itemIndex || !this.itemIndex.size) {
            for (const currentMechanicBooking of STATIC_MECHANIC_BOOKINGS) {
                if (currentMechanicBooking.id) {
                    this.itemIndex.set(currentMechanicBooking.id, currentMechanicBooking);
                }
            }
        }
        return this.itemIndex;
    }

    getScaleSettings(): Map<TimelineTimeAxisScaleType, ScaleType> {
        return MECHANICS_SCALE_SETTINGS;
    }

    getScaleSettingsSimplified(): Map<TimelineTimeAxisScaleType, ScaleType> {
        return MECHANICS_SCALE_SETTINGS_SIMPLIFIED;
    }

    getUniqueStoreKey(): string {
        return 'mechanicsplanningsimplified';
    }

    getGroupIndex(): Map<number | string, DataGroup> {
        if (!this.groupIndex || !this.groupIndex.size) {
            for (const mechanic of Mechanics) {
                if (mechanic.id) {
                    this.groupIndex.set(mechanic.id, mechanic);
                }
            }
        }
        return this.groupIndex;
    }

    getSuperGroupByGroup(groupKey: string | number): DataGroup | null {
        if (!this.superGroupByGroupIndex || !this.superGroupByGroupIndex.size) {
            for (const superGroup of REGIONS) {
                const nestedGroups = superGroup.nestedGroups;
                if (nestedGroups) {
                    for (const nestedGroup of nestedGroups) {
                        this.superGroupByGroupIndex.set(nestedGroup, superGroup);
                    }
                }
            }
        }
        return this.superGroupByGroupIndex.get(groupKey) || null;
    }

    getSuperGroupIndex(): Map<number | string, DataGroup> {
        if (!this.superGroupIndex || !this.superGroupIndex.size) {
            for (const superGroup of REGIONS) {
                this.superGroupIndex.set(superGroup.id, superGroup);
            }
        }
        return this.superGroupIndex;
    }

    getHasSimplifiedView(): boolean {
        return true;
    }

    registerObserver(observer: ObserverType) {
        this.dataObservers.push(observer);
    }

    private notifyObservers() {
        for (const observer of this.dataObservers) {
            observer.notify();
        }
    }
}

const MECHANICS_DATA_STORE_SERVICE_INSTANCE = new MechanicsTimelineDataStoreService();

export {MECHANICS_DATA_STORE_SERVICE_INSTANCE};
